import React, { useEffect, useState, useRef } from "react";
import "../../_css/StudentList.css";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { getAllPdfs, markStudentsAsReviewed, updateStudent } from "../../requests";
import { deleteAuthToken, notifySuccess } from "../../utils/helper";
import { notifyError } from "../helper/Toaster";
import { formatDateLong, formatGrade } from "../helper/formatters";
import CustomTooltip from "../common/CustomTooltip";
import { Info, TriangleAlert } from "lucide-react";

const StudentList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [totalCount, setTotalCount] = useState(0);

  const [students, setStudents] = useState([]);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);

  const get_students = async (pageVal) => {
    const filenameParam = new URLSearchParams(window.location.search).get(
      "filename"
    );
    const filename = filenameParam ? decodeURIComponent(filenameParam) : null;

    dispatch(startLoader());
    try {
      const res = await getAllPdfs({
        page: pageVal,
        limit: limit,
        filename: filename,
        name: name,
      });

      const students_data = res.data.data;

      setTotalCount(res.data.count);
      setStudents(students_data);
      saveAllIdsToLocalStorage(students_data);
    } catch (error) {
      if (error?.response?.status == 403) {
        notifyError(
          error?.response?.data?.error || "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }

      console.log(error);

      notifyError("Error fetching students");
    } finally {
      dispatch(stopLoader());
    }
  };

  const saveAllIdsToLocalStorage = (data) => {
    if (!Array.isArray(data)) return;
    const ids = data.map((e) => e.id).filter((studentId) => studentId != null);
    localStorage.setItem("allIds", JSON.stringify(ids));
  }

  const persistAllStudentIds = async () => {
    const filenameParam = new URLSearchParams(window.location.search).get(
      "filename"
    );
    const filename = filenameParam ? decodeURIComponent(filenameParam) : null;

    try {
      const res = await getAllPdfs({
        page: 1,
        limit: Math.max(Number(totalCount) || 0, students.length, 50),
        filename,
        name,
      });
      saveAllIdsToLocalStorage(
        res?.data?.data?.length ? res.data.data : students
      );
    } catch (error) {
      saveAllIdsToLocalStorage(students);
    }
  };

  const handleClick = async (id) => {
    dispatch(startLoader());
    await persistAllStudentIds();
    navigate(`/student-list/review-answers/${id}?view=comparison`);
    dispatch(stopLoader());
  };

  const handleEdit = (student) => {
    setEditingId(student.id);
    setEditingData({
      id: student.id,
      name: student.student_name,
      rollno: student.student_rollno,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData(null);
  };
  const markAllAsReviewed = async (filename) => {
    console.log("marking all as reviewed for filename:", filename);
    dispatch(startLoader());
    try {
      const response = await markStudentsAsReviewed(filename);
      notifySuccess(response?.data?.message || "All students marked as reviewed");
      get_students(page);
    } catch (error) {
      notifyError(error?.response?.data?.error || "Failed to mark all students as reviewed");
    } finally {
      dispatch(stopLoader());
    }
  };

  const handleSave = async () => {
    if (!editingData.name || editingData.name.trim() === "") {
      notifyError("Student name is required");
      return;
    }

    if (!editingData.rollno || editingData.rollno.trim() === "") {
      notifyError("Roll number is required");
      return;
    }

    dispatch(startLoader());
    try {
      const response = await updateStudent({
        name: editingData.name.trim(),
        rollno: editingData.rollno.trim(),
        id: editingData.id,
      });

      if (response.status < 400) {
        notifySuccess(response?.data?.message || "Student updated successfully");
        setEditingId(null);
        setEditingData(null);
        get_students(page);
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        notifyError(
          error?.response?.data?.error ||
          "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }
      notifyError(error?.response?.data?.error || "Failed to update student");
    } finally {
      dispatch(stopLoader());
    }
  };

  const timeoutRef = useRef(null);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout to call the API after 1.5 seconds
    timeoutRef.current = setTimeout(() => {
      setPage(1);
      get_students(1);
    }, 1500);

    // Cleanup function to clear timeout on unmount or when name changes again
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [name]);

  useEffect(() => {
    get_students(page);
  }, [page, limit]);

  return (
    <div className="student-list-main">
      <div className="student-list-container">
        <div className="student-list-head">
          <div className="student-list-head-text">Student List</div>
          <div style={{ display: "flex", gap: "10px" }}>
            {students.some(e => e.review_required) &&
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CustomTooltip
                  content={`Question Number ${[...new Set(students.flatMap(e => e.review_question_nos || []))].join(", ")} requires manual review.`}
                >
                  <Info />
                </CustomTooltip>

                <button
                  className="my-btn student-fb-btn"
                  disabled={students.every(e => !e.review_required)}
                  onClick={() => markAllAsReviewed(students[0].file_name)}
                >
                  Mark all as Reviewed
                </button>


              </div>
            }
            <input
              className="student-list-search"
              style={{ height: "32px" }}
              placeholder="Search name"
              name="name"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>
        <div className="student-list-notice">
          Please make sure to add <strong>student name</strong> &amp;{" "}
          <strong>roll no</strong> — this is very crucial for generating reports.
        </div>
        <div className="student-table">
          <div className="student-table-head">
            <div className="student-head-cell" style={{ width: "19%" }}>
              File Name
            </div>
            <div className="student-head-cell" style={{ width: "19%" }}>
              Student Name
            </div>
            <div className="student-head-cell" style={{ width: "12%" }}>
              AI Score
            </div>
            <div className="student-head-cell" style={{ width: "10%" }}>
              Class
            </div>
            <div className="student-head-cell" style={{ width: "10%" }}>
              Roll no
            </div>
            <div className="student-head-cell">Subject</div>
            <div className="student-head-cell" style={{ width: "12%" }}>
              Exam name
            </div>
            {/* <div className="student-head-cell" style={{ width: "17%" }}>
              Uploaded On
            </div> */}
            <div className="student-head-cell" style={{ width: "12%" }}>
              Action
            </div>
          </div>
          {students.length > 0 &&
            students.map((e, i) => {
              const isEditing = editingId === e.id;
              return (
                <div key={e.id} className="student-table-row">
                  <div className="student-row-cell" style={{ width: "19%" }}>

                    {e.file_name}
                  </div>
                  <div className="student-row-cell" style={{ width: "19%" }}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingData?.name || ""}
                        onChange={(e) =>
                          setEditingData({
                            ...editingData,
                            name: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "14px",
                          outline: "none",
                        }}
                      />
                    ) : (
                      <div>
                        {e.student_name}
                        {e?.review_required && <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px" }}>

                          <div className="metadata-item">
                            <span style={{
                              color: "#ac0000",
                              background: "#ffdddd",
                            }} className="badge badge-danger">
                              <TriangleAlert size={14} />

                            </span>
                          </div>
                          <CustomTooltip
                            content={`Question Number ${e.review_question_nos.join(", ")} requires manual review.`}
                          >
                            <span className="confidence-info-icon">
                              <Info size={14} />
                            </span>
                          </CustomTooltip>
                        </div>
                        }
                      </div>

                    )}
                  </div>
                  <div className="student-row-cell" style={{ width: "12%" }}>
                    {Number(e.marks_obt)} / {Number(e.marks_total)}
                  </div>
                  <div className="student-row-cell" style={{ width: "10%" }}>
                    {formatGrade(e.grade)}
                  </div>
                  <div className="student-row-cell" style={{ width: "10%" }}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingData?.rollno || ""}
                        onChange={(e) =>
                          setEditingData({
                            ...editingData,
                            rollno: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "14px",
                          outline: "none",
                        }}
                      />
                    ) : (
                      e.student_rollno
                    )}
                  </div>
                  <div className="student-row-cell">{e.subject}</div>
                  <div className="student-row-cell" style={{ width: "12%" }}>
                    {e.exam_name}
                  </div>
                  {/* <div className="student-row-cell" style={{ width: "16%" }}>
                    {formatDateLong(e.created_at)}
                  </div> */}
                  <div className="student-row-cell" style={{ width: "12%" }}>
                    {isEditing ? (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          onClick={handleSave}
                          className="my-btn"
                          style={{
                            backgroundColor: "#01A7DE",
                            color: "#FFFFFF",
                            padding: "6px 16px",
                            fontSize: "12px",
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="my-btn"
                          style={{
                            backgroundColor: "#E5E7EB",
                            color: "#030712",
                            padding: "6px 16px",
                            fontSize: "12px",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div>

                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            onClick={() => handleClick(e.id)}
                            className="student-fb-btn"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleEdit(e)}
                            className="my-btn"
                            style={{
                              backgroundColor: "#E0F5FE",
                              color: "#01A7DE",
                              padding: "6px 16px",
                              fontSize: "12px",
                            }}
                          >
                            Edit
                          </button>

                        </div>

                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
        <div className="di-flex" style={{ width: "100%" }}>
          <div style={{ color: "#6B7280" }}>
            {" "}
            Showing {((page - 1) * limit) + 1} to {students.length + (page - 1) * limit} out of {totalCount}{" "}
            items{" "}
          </div>
          <div
            className="di-flex"
            style={{ justifyContent: "end", gap: "10px" }}
          >
            <button
              disabled={page == 1}
              className="my-btn"
              style={{
                backgroundColor: "#E0F5FE",
                color: "#01A7DE",
                opacity: page == 1 ? 0.4 : 1,
              }}
              onClick={() => setPage((p) => (p > 1 ? p - 1 : p))}
            >
              Previous
            </button>
            <button
              disabled={students.length + (page - 1) * limit >= totalCount}
              className="my-btn"
              style={{
                backgroundColor: "#E0F5FE",
                color: "#01A7DE",
                opacity:
                  students.length + (page - 1) * limit >= totalCount ? 0.4 : 1,
              }}
              onClick={() => setPage((p) => (students.length ? p + 1 : p))}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentList;
